import { Box, Grid, Text } from '@chakra-ui/react';
import { WIDGET_DEFAULT_FONT } from 'constants/fonts';
import { CLICK_ON_PAID_FEATURE, FONT_STYLE_APPLIED, UPGRADE } from 'constants/mixpanel';
import { loadFonts } from 'hooks/useFont';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { dispatchGlobalApplyThemeEvent } from 'modules/Editor/Editor.helpers';
import { useBoundingBox } from 'modules/Editor/components/BoundingBox/useBoundingBox';
import { setActiveWidget, store, useAppSelector } from 'modules/Editor/store';
import { selectBrandIdx } from 'modules/Editor/store/editorSettingsSelector';
import { selectInfograph, selectWidgets } from 'modules/Editor/store/infographSelector';
import { applyFont as applyFontAction } from 'modules/Editor/store/infographSlice';
import { UserTypes, useBrand } from 'modules/User';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { BrandKitRedirectButton } from '../BrandKitTab/BrandKitRedirectButton';
import { EXPLORE_FONTS_CONFIG, SHOW_LOADER_WIDGET_COUNT_TRESHOLD } from './ApplyFonts.config';
import { applyFont, formatUploadedFontName, getInitalExploreFonts, labelTextWidgets } from './ApplyFonts.helpers';
import { ApplyFont } from './ApplyFonts.types';
import { ApplyFontsButton, ApplyFontsButtonByType } from './ApplyFontsButton';

// import { SuggestionsFont } from './SuggestionsFont';

const useApplyFonts = ({ isBrandKit }: { isBrandKit?: boolean } = {}) => {
  const { boundingBox } = useBoundingBox();
  const isLoadingRef = useRef(false);

  const onApplyFonts = useCallback(
    (fontConfig: ApplyFont, showLoader: (showLoader: boolean) => void) => {
      const infograph = selectInfograph(store.getState());

      const applyFontType = Object.keys(fontConfig);

      const resetLoading = () => {
        isLoadingRef.current = false;
        showLoader(false);
      };

      // categorize the text-widgets into title, heading, and paragraph
      let labeledTextWidgets = labelTextWidgets(infograph);
      if (applyFontType.length === 1) {
        labeledTextWidgets = labeledTextWidgets.filter((widget) => widget.textType === applyFontType[0]);
      }
      if (!labeledTextWidgets.length) return resetLoading();

      // apply font family
      const widgets = selectWidgets(store.getState());
      const updateWidgets = applyFont(widgets, labeledTextWidgets, fontConfig);
      if (!updateWidgets.length) return resetLoading();

      Mixpanel.track(FONT_STYLE_APPLIED.eventName, {
        type: isBrandKit ? FONT_STYLE_APPLIED.type.BrandKit : FONT_STYLE_APPLIED.type.Generic,
      });
      store.dispatch(setActiveWidget([]));
      store.dispatch(applyFontAction(updateWidgets));

      dispatchGlobalApplyThemeEvent();
      setTimeout(() => {
        boundingBox.updateRect();
        resetLoading();
      }, 0);
    },
    [boundingBox, isBrandKit],
  );

  const onClick = useCallback(
    (fonts: ApplyFont, showLoader: (showLoader: boolean) => void) => {
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;

      // The apply fonts can be a bit slow if there are a lot of widgets - show a loader in that case
      const shouldShowLoader = Object.keys(selectWidgets(store.getState())).length > SHOW_LOADER_WIDGET_COUNT_TRESHOLD;
      if (shouldShowLoader) {
        showLoader(true);

        // Force applyFonts to run in the next frame since it can block the UI (and the loader won't show up)
        new Promise((resolve) => {
          requestAnimationFrame(() => setTimeout(resolve, 0));
        }).then(() => {
          onApplyFonts(fonts, showLoader);
        });
      } else {
        onApplyFonts(fonts, showLoader);
        isLoadingRef.current = false;
      }
    },
    [onApplyFonts],
  );

  return { onClick };
};

export const BrandKitFonts = ({
  hasBrandKitPermission,
  hasBrandKitEditPermission,
  onOpenUpgradeModal,
}: {
  hasBrandKitPermission: boolean;
  hasBrandKitEditPermission: boolean;
  onOpenUpgradeModal: (mpFeatureName: string) => void;
}) => {
  const brandIdx = useAppSelector(selectBrandIdx);
  const { data: brandData } = useBrand();
  const selectedBrand = brandData?.[brandIdx];
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.brandKit' });

  const { onClick } = useApplyFonts({ isBrandKit: true });

  const titleFont = selectedBrand?.title?.fontFamily || WIDGET_DEFAULT_FONT;
  const headingFont = selectedBrand?.subtitle?.fontFamily || WIDGET_DEFAULT_FONT;
  const paragraphFont = selectedBrand?.text?.fontFamily || WIDGET_DEFAULT_FONT;
  const hasBrandFont =
    selectedBrand?.title?.fontFamily || selectedBrand?.subtitle?.fontFamily || selectedBrand?.text?.fontFamily;

  // custom fonts have a different display name
  const titleDisplayName = selectedBrand?.title?.isUploadedFont
    ? formatUploadedFontName(selectedBrand.title.fontName)
    : titleFont;
  const headingDisplayName = selectedBrand?.subtitle?.isUploadedFont
    ? formatUploadedFontName(selectedBrand.subtitle.fontName)
    : headingFont;
  const paragraphDisplayName = selectedBrand?.text?.isUploadedFont
    ? formatUploadedFontName(selectedBrand.text.fontName)
    : paragraphFont;

  const onGatedModalOpen = () => {
    Mixpanel.track(CLICK_ON_PAID_FEATURE.eventName, {
      Feature: UPGRADE.feature.BrandkitFont,
      plan: UserTypes.Business,
    });

    onOpenUpgradeModal(UPGRADE.feature.BrandkitFont);
  };

  if (!hasBrandFont) {
    return (
      <BrandKitRedirectButton
        title={t('addFont')}
        text={t('addFontText')}
        hasBrandEditPermission={hasBrandKitEditPermission}
      />
    );
  }

  return (
    <Box>
      <Text fontSize='xs' fontWeight='medium'>
        {t('addedFonts')}
      </Text>
      <ApplyFontsButtonByType
        hasBrandKitPermission={hasBrandKitPermission}
        title={titleFont}
        heading={headingFont}
        paragraph={paragraphFont}
        onClick={!hasBrandKitPermission ? onGatedModalOpen : onClick}
        titleDisplayName={titleDisplayName}
        headingDisplayName={headingDisplayName}
        paragraphDisplayName={paragraphDisplayName}
      />
      {/* TODO: Implement logic for suggest font */}
      {/* <SuggestionsFont /> */}
    </Box>
  );
};

// declared outside so we only call loadFonts once
let hasLoadedFonts = false;

export const ExploreFonts = () => {
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.brandKit' });
  const { onClick } = useApplyFonts();

  // load the fonts used in Explore fonts (only once)
  if (!hasLoadedFonts) loadFonts(getInitalExploreFonts());

  return (
    <>
      <Text fontSize='sm' fontWeight='semibold' py={1} px={4} mb={3}>
        {t('exploreFonts')}
      </Text>
      <Grid mx={4} templateColumns='repeat(2, 1fr)' gap={3}>
        {EXPLORE_FONTS_CONFIG.map((font) => {
          const key = font.title + font.heading + font.paragraph;

          return (
            <ApplyFontsButton
              key={key}
              title={font.title}
              heading={font.heading}
              paragraph={font.paragraph}
              titleStyle={font.titleStyle}
              onClick={onClick}
            />
          );
        })}
      </Grid>
    </>
  );
};
